import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_ARRAY_LENGTH, FIRST_ELEMENT_INDEX, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import {
    GetAccountingBasisTranslationDto,
    GetFinancialLineItemTranslationDto,
    GetLegalFormTranslationDto,
} from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'aumico-settings-edit-settings-translations',
    templateUrl: './edit-settings-translations.component.html',
    styleUrls: ['./edit-settings-translations.component.scss'],
})
export class EditSettingsTranslationsComponent {
    public formGroup: UntypedFormGroup | undefined;
    public translationsFormArray: UntypedFormArray | undefined;
    public displayedColumns: string[] = ['name', 'currentLocale', 'selectedLocale'];
    public isCompleted: boolean;

    private tableDataValues:
        | GetFinancialLineItemTranslationDto[]
        | GetAccountingBasisTranslationDto[]
        | GetLegalFormTranslationDto[] = [];
    @Input() set tableData(
        value: GetFinancialLineItemTranslationDto[] | GetAccountingBasisTranslationDto[] | GetLegalFormTranslationDto[],
    ) {
        if (value != null) {
            this.tableDataValues = value;
            this.buildForm();
        }
    }

    public selectedLocaleValue: string | undefined;
    @Input() set selectedLocale(value: string | undefined) {
        if (value != null) {
            this.selectedLocaleValue = value;
            this.buildForm();
        }
    }

    @Input() currentLocale = 'de-ch';
    @Input() panelTitleKey: string;
    @Input() panelOpenState = false;

    @Output() translationValuesChanged: EventEmitter<
        GetFinancialLineItemTranslationDto[] | GetAccountingBasisTranslationDto[] | GetLegalFormTranslationDto[]
    > = new EventEmitter<
        GetFinancialLineItemTranslationDto[] | GetAccountingBasisTranslationDto[] | GetLegalFormTranslationDto[]
    >();

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
    ) {
        this.addSvgIcon('aumico-pattern-icon-check-circle');
        this.addSvgIcon('aumico-pattern-icon-wrong-circle');
        this.buildForm();
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private buildForm(): void {
        this.formGroup = this.formBuilder.group({
            translationsFormArray: this.formBuilder.array([]),
        });
        this.translationsFormArray = this.getAllAsFormArray();

        this.monitorFormChanges();

        this.formGroup.setControl('translationsFormArray', this.translationsFormArray);
        this.formGroup.updateValueAndValidity();
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups: AbstractControl[] = this.tableDataValues.map((entry) => this.getTableEntryFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private getTableEntryFormGroup(
        tableEntry: GetFinancialLineItemTranslationDto | GetAccountingBasisTranslationDto | GetLegalFormTranslationDto,
    ): UntypedFormGroup {
        const selectedLocaleValue = this.selectedLocaleValue.toLowerCase();
        const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
        dataSourceFormGroup['name'] = new UntypedFormControl(tableEntry['name']);
        dataSourceFormGroup['_id'] = new UntypedFormControl(tableEntry['_id']);
        if ('text' in tableEntry) {
            dataSourceFormGroup[this.currentLocale] = new UntypedFormControl(tableEntry['text'][this.currentLocale]);
            dataSourceFormGroup[this.selectedLocaleValue] = new UntypedFormControl(
                tableEntry['text'][selectedLocaleValue],
            );
        } else if ('description' in tableEntry) {
            dataSourceFormGroup[this.currentLocale] = new UntypedFormControl(
                tableEntry['description'][this.currentLocale],
            );
            dataSourceFormGroup[this.selectedLocaleValue] = new UntypedFormControl(
                tableEntry['description'][selectedLocaleValue],
            );
        }

        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe((formValue) => {
                const translationResults = [];

                if (this.tableDataValues[FIRST_ELEMENT_INDEX] == undefined) {
                    return;
                }

                formValue.translationsFormArray.forEach((value) => {
                    if ('text' in this.tableDataValues[FIRST_ELEMENT_INDEX]) {
                        translationResults.push({
                            _id: value._id,
                            name: value.name,
                            text: {
                                [this.currentLocale]: value[this.currentLocale],
                                [this.selectedLocaleValue]: value[this.selectedLocaleValue],
                            },
                        });
                    } else if ('description' in this.tableDataValues[FIRST_ELEMENT_INDEX]) {
                        translationResults.push({
                            _id: value._id,
                            name: value.name,
                            description: {
                                [this.currentLocale]: value[this.currentLocale],
                                [this.selectedLocaleValue]: value[this.selectedLocaleValue],
                            },
                        });
                    }
                });

                this.translationValuesChanged.emit(translationResults);

                if (
                    this.tableDataValues.length > EMPTY_ARRAY_LENGTH &&
                    'text' in this.tableDataValues[FIRST_ELEMENT_INDEX] &&
                    this.selectedLocaleValue != null
                ) {
                    this.checkIfTranslationsTextsAreCompleted(translationResults);
                } else if (
                    this.tableDataValues.length > EMPTY_ARRAY_LENGTH &&
                    'description' in this.tableDataValues[FIRST_ELEMENT_INDEX] &&
                    this.selectedLocaleValue != null
                ) {
                    this.checkIfTranslationsDescriptionsAreCompleted(translationResults);
                } else if (this.tableDataValues.length === EMPTY_ARRAY_LENGTH) {
                    this.isCompleted = true;
                }
            });
    }

    private checkIfTranslationsDescriptionsAreCompleted(
        translationResults: GetFinancialLineItemTranslationDto[],
    ): void {
        this.isCompleted = true;
        translationResults.forEach((translation) => {
            if (
                translation['description'] != null &&
                translation['description'][this.selectedLocaleValue] != null &&
                translation['description'][this.selectedLocaleValue].length > EMPTY_ARRAY_LENGTH &&
                translation['description'][this.currentLocale] != null &&
                translation['description'][this.currentLocale].length > EMPTY_ARRAY_LENGTH
            ) {
                this.isCompleted = this.isCompleted && true;
            } else {
                this.isCompleted = false;
            }
        });
    }

    private checkIfTranslationsTextsAreCompleted(
        translationResults: GetAccountingBasisTranslationDto[] | GetLegalFormTranslationDto[],
    ): void {
        this.isCompleted = true;
        translationResults.forEach((translation) => {
            if (
                translation['text'] != null &&
                translation['text'][this.selectedLocaleValue] != null &&
                translation['text'][this.selectedLocaleValue].length > EMPTY_ARRAY_LENGTH &&
                translation['text'][this.currentLocale] != null &&
                translation['text'][this.currentLocale].length > EMPTY_ARRAY_LENGTH
            ) {
                this.isCompleted = this.isCompleted && true;
            } else {
                this.isCompleted = false;
            }
        });
    }
}
